import { __decorate, __metadata } from "tslib";
import { I18nThemed } from 'interfaces/mixins';
import { lccstore } from 'lcc-state';
import timesync from 'timesync';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import has from '@dojo/framework/core/has';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import Button from '@dojo/widgets/button';
import TextInput from '@dojo/widgets/text-input';
import css from '../css/mcquestion.m.css';
import neCss from '../css/numeric-entry.m.css';
import { setQuestion } from '../support/state';
import { Correct } from '../widgets/correct';
const ISSEEN = 0;
const ISCOMPLETE = 1;
const ISCORRECT = 2;
const HINTBACK = 3;
let NumericEntryBase = class NumericEntryBase extends I18nThemed {
    _propsInited = false;
    _lastQuestionId = '';
    _inSubmit = false;
    _inFinish = false;
    _inNext = false;
    modifyQuestion(htmlText) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlText;
        const strongTag = tempDiv.querySelector('strong');
        if (strongTag) {
            const parentNode = strongTag.parentNode;
            const h2Tag = document.createElement('h2');
            h2Tag.appendChild(strongTag);
            parentNode?.insertBefore(h2Tag, parentNode.firstChild);
        }
        const divElements = tempDiv.querySelectorAll('div');
        const lastDiv = divElements[divElements.length - 1];
        if (lastDiv) {
            lastDiv.setAttribute('id', 'question-label');
        }
        const modifiedText = tempDiv.innerHTML;
        return modifiedText;
    }
    onChange(value) {
        const { question, quizstore } = this.properties;
        question.answer = value;
        setQuestion(quizstore)({ question });
        this.invalidate();
    }
    onAttach() {
        lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
    }
    ensure_nls(properties) {
        const { get, path } = lccstore;
        const dynls = get(path('dynls'));
        const bundle = dynls.get('quiz');
        if (!bundle) {
            properties.localizedBundle = { isPlaceholder: true, format: null, messages: {} };
        }
        else {
            properties.localizedBundle = this.localizeBundle(bundle);
        }
        return properties;
    }
    update_result() {
        const { question, updater } = this.properties;
        const { icode, status, cursor, answer } = question;
        question.flags[ISSEEN] = true;
        question.flags[ISCOMPLETE] = !!answer;
        const data = {
            answer,
            icode: { ...icode, _answer: answer },
            starttime: question.starttime,
            stoptime: timesync.now().toJSON(),
            isseen: true,
            ismarked: question.marked ? true : false,
            iscorrect: question.flags[ISCORRECT]
        };
        const qrlocator = {
            status,
            cursor,
            qrid: '',
            quiz: ''
        };
        const result = { qrlocator, data };
        return updater([result]);
    }
    validateAnswer() {
        const { question } = this.properties;
        const { icode = {}, answer, ans, tol } = question;
        const _raw = ans && ans in icode ? icode[ans].raw : undefined;
        const _correct = _raw ? Number(_raw) : undefined;
        const _answer = answer ? Number(answer) : undefined;
        const _tol = tol ? Number(tol) : 0;
        if (_answer === undefined || _correct === undefined)
            return false;
        const ceiling = _correct + _tol;
        const floor = _correct - _tol;
        return _answer >= floor && _answer <= ceiling;
    }
    set_iscorrect() {
        const { question } = this.properties;
        question.flags[ISCORRECT] = this.validateAnswer();
    }
    submitted() {
        this.update_result();
        const { question, quizstore } = this.properties;
        question.status = 'feedback';
        setQuestion(quizstore)({ question });
        this.invalidate();
    }
    get_numberofquestions() {
        const { quizstore } = this.properties;
        const { get, path } = quizstore;
        return get(path('info')).numberofquestions;
    }
    render_hint() {
        const { question, localizedBundle } = this.properties;
        const { ans, icode = {} } = question;
        const { hint = '[quiz (numeric entry)] hint' } = localizedBundle.messages;
        const _raw = ans && ans in icode ? icode[ans].raw : undefined;
        const showflag = has('use-hints') && _raw;
        return showflag ? `${hint}: ${_raw}` : '';
    }
    check_hintback() {
        const { question } = this.properties;
        const { hintback, hintbackstatus, answer } = question;
        if (hintbackstatus === 'initial') {
            const incorrect = !question.flags[ISCORRECT] || false;
            question.flags[HINTBACK] = (Boolean(!answer) || incorrect) && hintback !== '<p>\n</p>';
            question.hintbackstatus = 'checked';
        }
    }
    render_hintback() {
        const { question, localizedBundle } = this.properties;
        const { hintback } = question;
        const { hint = '[quiz (numeric entry)] hint' } = localizedBundle.messages;
        question.hintbackstatus = 'rendered';
        return v('div', { key: 'hintback', classes: [neCss.hintback], xqa: 'hintback' }, [
            `${hint}:`,
            v('div', { innerHTML: hintback })
        ]);
    }
    render_buttons(which) {
        const { messages: { finish: finishText = '', next_question: nextText = '', submit: submitText = '' } } = this.properties.localizedBundle;
        const { onNextQuestion, onFinish, theme, question } = this.properties;
        const { valid } = question;
        const { islast = false, isfeedback = false } = which;
        const disabled = valid ? !valid?.valid : false;
        const buttons = isfeedback
            ?
                islast
                    ? w(Button, {
                        theme,
                        key: 'finish',
                        onClick: async () => {
                            if (!this._inFinish) {
                                this._inFinish = true;
                                this.set_iscorrect();
                                await this.update_result();
                                onFinish();
                            }
                        }
                    }, [finishText])
                    : w(Button, {
                        theme,
                        key: 'next',
                        onClick: () => {
                            if (!this._inNext) {
                                this._inNext = true;
                                this.update_result();
                                onNextQuestion();
                            }
                        }
                    }, [nextText])
            :
                w(Button, {
                    theme,
                    disabled,
                    key: 'submit',
                    onClick: () => {
                        this.set_iscorrect();
                        this.check_hintback();
                        if (question.hintbackstatus === 'checked' && question.flags[HINTBACK]) {
                            question.hintbackstatus = 'render';
                            this.invalidate();
                        }
                        else if (!this._inSubmit) {
                            this._inSubmit = true;
                            this.submitted();
                        }
                    }
                }, [submitText]);
        return [buttons];
    }
    render_question() {
        const { question } = this.properties;
        const { messages: { answerHelp = '[quiz (numeric entry)] answerHelp' } } = this.properties.localizedBundle;
        const { text, id, questiontype, valid, answer } = question;
        const numberofquestions = this.get_numberofquestions();
        const islast = question.cursor + 1 === numberofquestions;
        const modifiedText = this.modifyQuestion(text);
        return v('div', { key: id, xqa: `${questiontype} ${id}`, classes: [this.theme(css.root)] }, [
            v('div', { key: 'question-text', classes: [this.theme(css.question)], innerHTML: modifiedText }),
            question.hintbackstatus === 'render' ? this.render_hintback() : null,
            w(TextInput, {
                valid: valid || { valid: true },
                onValidate: (valid, message) => {
                    question.valid = { valid, message };
                },
                value: answer || '',
                placeholder: this.render_hint(),
                pattern: '^$|^-?(\\d+)?(\\.?\\d*)?$',
                helperText: answerHelp,
                onValue: (value) => this.onChange(value)
            }),
            v('div', { key: 'action-block', xqa: 'action block', classes: [this.theme(css.actions)] }, [
                v('div', { key: 'submit-action', xqa: 'action submit', classes: [this.theme(css.oneaction)] }, this.render_buttons({ islast }))
            ])
        ]);
    }
    render_feedback() {
        const { messages: { correct: correctText = '', incorrect: incorrectText = '', rationale: rationaleText = '', for_more_info: fmiText = '', noAnswerProvided = '[quiz (numeric entry)] noAnswerProvided' }, format } = this.properties.localizedBundle;
        const { question } = this.properties;
        const { text, feedback, refname, id, questiontype, answer } = question;
        const numberofquestions = this.get_numberofquestions();
        const islast = question.cursor + 1 === numberofquestions;
        const iscorrect = question.flags[ISCORRECT];
        const modifiedText = this.modifyQuestion(text);
        return v('div', { key: id, xqa: `${questiontype} ${id}`, classes: [this.theme(css.root)] }, [
            v('div', { key: 'feedback-block', classes: [this.theme(css.feedback)] }, [
                v('h2', { key: 'rationale', classes: [this.theme(css.rationale)] }, [rationaleText]),
                v('div', { key: 'correct', classes: [] }, [
                    v('span', { key: 'you-answered' }, [
                        answer ? format('you_answered', { formattedCandidateAnswer: answer }) : noAnswerProvided,
                        w(Correct, { iscorrect, title: iscorrect ? correctText : incorrectText })
                    ])
                ]),
                v('div', { innerHTML: feedback }),
                v('p', { classes: [this.theme(css.fmi)] }, [`${fmiText} ${refname}`])
            ]),
            v('div', {
                key: 'question-text',
                classes: [this.theme(css.question)],
                innerHTML: modifiedText
            }),
            w(TextInput, {
                value: answer || '',
                disabled: true
            }),
            v('div', { key: 'action-block', xqa: 'action block', classes: [this.theme(css.actions)] }, [
                v('div', { key: 'submit-action', xqa: 'action submit', classes: [this.theme(css.oneaction)] }, this.render_buttons({ islast, isfeedback: true }))
            ])
        ]);
    }
    render() {
        const { isPlaceholder } = this.properties.localizedBundle;
        if (isPlaceholder) {
            return null;
        }
        const { question } = this.properties;
        if (!question) {
            return null;
        }
        const { status } = question;
        return status === 'question' ? this.render_question() : this.render_feedback();
    }
    stateProperties(properties) {
        const { quizstore } = properties;
        const { get, path } = quizstore;
        if (!this._propsInited) {
            const paths = [['question'], ['quiz'], ['loaded']];
            const invalidatePaths = paths.map((path) => quizstore.path(path.join('/')));
            const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
            this.own({
                destroy: () => {
                    handle.remove();
                }
            });
            const h2 = lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
            this.own({
                destroy: () => {
                    h2.remove();
                }
            });
            this._propsInited = true;
        }
        properties.quizkind = get(path('quiz')).kind;
        properties.question = get(path('question'));
        if (properties.question?.id !== this._lastQuestionId) {
            this._lastQuestionId = properties.question?.id || '';
            this._inSubmit = false;
            this._inFinish = false;
            this._inNext = false;
        }
        const { hintbackstatus } = properties.question;
        properties.question.hintbackstatus = hintbackstatus === undefined ? 'initial' : hintbackstatus;
        properties.loaded = get(path('loaded'));
        return properties;
    }
};
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], NumericEntryBase.prototype, "ensure_nls", null);
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], NumericEntryBase.prototype, "stateProperties", null);
NumericEntryBase = __decorate([
    theme(css)
], NumericEntryBase);
export { NumericEntryBase };
export default NumericEntryBase;
